import {MEDIUM_BREAKPOINT} from '@wandb/weave/common/css/globals.styles';
import React, {memo, useCallback, useEffect, useState} from 'react';
import {useHistory, useParams} from 'react-router';

import {Content} from '../../components/PageWithSidebar/PageWithSidebar.styles';
import {hasTraceBackend} from '../../config';
import {useAccountTeamsList} from '../../pages/HomePage/HomePageSidebar/useAccountTeamsList';
// eslint-disable-next-line wandb/no-deprecated-imports
import * as Track from '../../services/analytics';
import {QuickStartAnalyticsOrigin} from '../../services/analytics/quickStartEvents';
import {useViewer} from '../../state/viewer/hooks';
import {Viewer} from '../../state/viewer/types';
import * as S from './QuickStart.styles';
import {QuickStartModels} from './QuickStartModels';
import {Product, QuickStartProductSelect} from './QuickStartProductSelect';
import {QuickStartWeave} from './QuickStartWeave/QuickStartWeave';

const DEFAULT_TABLET_OFFSET = 350; // Sidebar in quickstart and team page
const PROJECT_PAGE_TABLET_OFFSET = 100;

type QuickStartProps = {
  analyticsOrigin: QuickStartAnalyticsOrigin; // defaults to quickstart on home page
};

const QuickStartComp = ({
  analyticsOrigin = QuickStartAnalyticsOrigin.QUICKSTART_PAGE,
}: QuickStartProps) => {
  const viewer: Viewer | undefined = useViewer();
  const params = useParams<{entityName?: string; projectName?: string}>();
  const defaultEntityName = useUserSpecifiedDefaultEntity();
  const selectedAccountDefaultEntity = useSelectedAccountDefaultEntity();
  const entityName = params.entityName ?? defaultEntityName ?? '';
  const projectName = params.projectName ?? '';
  // `targetEntityForProjectCreation` is distinct from `entityName` because
  // it takes into account the currently selected account in the account selector.
  // This is the entity for which any quickstart CTAs should create new projects,
  // or for which the quickstart should be run. The Weave quickstart uses this pattern
  // as of this writing, but the Models quickstart uses `entityName`. I (Tim) did not
  // want to change Models quickstart behavior.
  // Precedence: (URL-specified) entityName > selectedAccountDefaultEntity > defaultEntityName)
  const targetEntityForProjectCreation =
    params.entityName ??
    selectedAccountDefaultEntity ??
    defaultEntityName ??
    '';

  // Tablet width + sidebar offsets depending on which page this comp is rendered in
  const maxWidth =
    MEDIUM_BREAKPOINT +
    (analyticsOrigin === QuickStartAnalyticsOrigin.PROJECT_PAGE
      ? PROJECT_PAGE_TABLET_OFFSET
      : DEFAULT_TABLET_OFFSET);

  const history = useHistory();
  const userInfoProduct = viewer?.userInfo?.wandbProductType;
  const searchParams = new URLSearchParams(window.location.search);
  const searchParamProduct = searchParams.get('product');

  const productString = searchParamProduct ?? userInfoProduct;
  // default to weave unless models is specified or no weave available
  const product =
    productString === Product.Models || !hasTraceBackend
      ? Product.Models
      : Product.Weave;
  const onSelectProduct = (newProduct: Product) => {
    searchParams.set('product', newProduct);
    history.replace({search: searchParams.toString()});
  };

  // Track which quickstart variants are shown, and attempt to only fire one analytics event per variant.
  const [variant, setVariant] = useState<
    | {
        product: Product;
        variant?: string;
      }
    | undefined
  >(undefined);

  const [trackedVariants, setTrackedVariants] = useState<
    Record<string, boolean>
  >({});

  const onVariantActivated = useCallback(
    (product: Product, variant: string) => {
      setVariant({product, variant});
    },
    [setVariant]
  );

  useEffect(() => {
    const variantKey = `${variant?.product}-${variant?.variant}`;

    if (variant && !trackedVariants[variantKey]) {
      setTrackedVariants(prev => ({
        ...prev,
        [variantKey]: true,
      }));

      Track.quickStartViewed({
        projectName,
        entityName: targetEntityForProjectCreation,
        location: analyticsOrigin,
        quickstartType: variant.product,
        quickstartVariant: variant.variant,
      });
    }
  }, [
    variant,
    projectName,
    analyticsOrigin,
    trackedVariants,
    targetEntityForProjectCreation,
  ]);

  return (
    <Content background="white">
      <S.QuickStartContainer $maxWidth={maxWidth} data-test="quickstart">
        <S.QuickStart>
          {hasTraceBackend && (
            <QuickStartProductSelect
              selected={product}
              onSelectProduct={onSelectProduct}
            />
          )}
          {product === Product.Models ? (
            <QuickStartModels
              entityName={entityName}
              projectName={projectName}
              analyticsOrigin={analyticsOrigin}
              maxWidth={maxWidth}
              onVariantActivated={onVariantActivated}
            />
          ) : (
            <QuickStartWeave
              entityName={targetEntityForProjectCreation}
              projectName={projectName}
              analyticsOrigin={analyticsOrigin}
              maxWidth={maxWidth}
              onVariantActivated={onVariantActivated}
            />
          )}
        </S.QuickStart>
      </S.QuickStartContainer>
    </Content>
  );
};

const useSelectedAccountDefaultEntity = (): string | null => {
  // Returns the first (default) team in the selected account.
  const {accountTeams} = useAccountTeamsList();
  const teams = accountTeams?.edges?.map(team => team?.node) ?? [];
  const firstTeamInSelectedAccount = teams.length > 0 ? teams[0]?.name : null;
  return firstTeamInSelectedAccount;
};

const useUserSpecifiedDefaultEntity = (): string | null => {
  // Returns the default entity name from the viewer's account.
  // This is slightly legacy behavior particularly for personal accounts.
  const viewer: Viewer | undefined = useViewer();
  return viewer?.defaultEntity?.name ?? null;
};

export const QuickStart = memo(QuickStartComp);
export default QuickStart;
